import { useId, useState, type FormEvent, type KeyboardEvent } from 'react';

import type { CustomerPause } from '../api/schemas';
import { MAX_PAUSE_REASON_LENGTH, panamaToday, pauseDayLabel, pauseProblem, pauseState } from '../lib/customerFile';
import { FormField } from './FormField';

type CustomerPauseRowProps = {
  pause: CustomerPause | null;
  /** A new pause, or null to lift the one in place. */
  onSave: (pause: CustomerPause | null) => void;
};

/**
 * A client who travels is not a client gone quiet: the team pauses the ficha
 * for the dates and Clientes stops counting them as inactive. One row: the
 * pause running or coming up with a way to lift it, or the way to set one.
 */
export function CustomerPauseRow({ pause, onSave }: CustomerPauseRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const today = panamaToday(new Date());

  if (isEditing) {
    return (
      <PauseForm
        today={today}
        onCancel={() => setIsEditing(false)}
        onSave={(next) => {
          setIsEditing(false);
          onSave(next);
        }}
      />
    );
  }

  const state = pauseState(pause, today);
  if (pause && state) {
    const title =
      state === 'active'
        ? `En pausa hasta ${pauseDayLabel(pause.until, today)}`
        : `Pausa del ${pauseDayLabel(pause.from, today)} al ${pauseDayLabel(pause.until, today)}`;
    return (
      <div className="row">
        <div className="grow">
          <p className="row-title">{title}</p>
          {pause.reason ? <p className="row-sub">{pause.reason}</p> : null}
        </div>
        <button type="button" className="btn small secondary" onClick={() => onSave(null)}>
          Quitar pausa
        </button>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="grow">
        <p className="customer-file-label">Pausa</p>
        <p className="row-sub">Si viaja, pausa su ficha y no cuenta como inactivo.</p>
      </div>
      <button type="button" className="btn small secondary" onClick={() => setIsEditing(true)}>
        Pausar
      </button>
    </div>
  );
}

type PauseFormProps = {
  today: string;
  onCancel: () => void;
  onSave: (pause: CustomerPause) => void;
};

/** From today by default, until a day the team picks, and an optional reason. Checked as the API checks it. */
function PauseForm({ today, onCancel, onSave }: PauseFormProps) {
  const id = useId();
  const [from, setFrom] = useState(today);
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<{ field: 'from' | 'until'; message: string } | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const message = pauseProblem(from, until);
    if (message) {
      setProblem({ field: from ? 'until' : 'from', message });
      return;
    }
    onSave({ from, until, reason: reason.trim() || null });
  };

  return (
    <form className="customer-glance-edit" aria-label="Pausar cliente" noValidate onSubmit={submit} onKeyDown={escapeTo(onCancel)}>
      <div className="form-grid cols-2">
        <FormField
          label="Desde"
          htmlFor={`${id}-from`}
          error={problem?.field === 'from' ? problem.message : undefined}
        >
          <input
            id={`${id}-from`}
            type="date"
            className="field-input"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </FormField>
        <FormField
          label="Hasta"
          htmlFor={`${id}-until`}
          error={problem?.field === 'until' ? problem.message : undefined}
        >
          <input
            id={`${id}-until`}
            type="date"
            className="field-input"
            value={until}
            min={from || undefined}
            onChange={(event) => setUntil(event.target.value)}
          />
        </FormField>
        <FormField label="Motivo (opcional)" htmlFor={`${id}-reason`} className="span-2">
          <input
            id={`${id}-reason`}
            className="field-input"
            value={reason}
            maxLength={MAX_PAUSE_REASON_LENGTH}
            placeholder="Ej.: Viaje"
            onChange={(event) => setReason(event.target.value)}
          />
        </FormField>
      </div>
      <div className="row-actions">
        <button type="submit" className="btn small">
          Guardar pausa
        </button>
        <button type="button" className="btn small secondary" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

/** Escape closes an inline edit without saving, and stops there so the drawer around it stays open. */
export function escapeTo(onCancel: () => void) {
  return (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    onCancel();
  };
}
